import { useEffect, useRef, useState } from "react";
import { Send, Phone, Wrench } from "lucide-react";
import WhatsAppTelCompanion from "@/components/WhatsAppTelCompanion";
import { resolveApiBaseUrl, trackEvent } from "@/utils/tracking";
import {
  getRecaptchaTokenResult,
  invalidateRecaptchaPublicConfig,
  isRecaptchaClientSetupErrorMessage,
  prefetchRecaptchaPublicConfig,
  shouldShowRecaptchaPublicNotice,
} from "@/utils/recaptcha";
import {
  compressBookingImageForUpload,
  BOOKING_ATTACHMENT_MAX_IMAGE_BEFORE_COMPRESS,
} from "@/utils/bookingAttachmentImage";
const BOOKING_TALON_MAX_BYTES = 5 * 1024 * 1024;

function normalizeTalonExt(filename) {
  const m = /\.([a-z0-9]+)$/i.exec(String(filename || ""));
  if (!m) return "";
  const e = String(m[1]).toLowerCase();

  return e === "jpeg" ? "jpg" : e;
}

const PHONE_REGEX = /^[0-9+\s().-]{7,32}$/;
export default function Booking({ t, initialService = "", copyOverrides = null }) {
  const bookingCopy = {
    ...t.booking,
    ...(copyOverrides || {}),
    labels: {
      ...t.booking.labels,
      ...(copyOverrides?.labels || {}),
    },
    placeholders: {
      ...t.booking.placeholders,
      ...(copyOverrides?.placeholders || {}),
    },
  };
  const [form, setForm] = useState({
    name: "", email: "", phone: "", car: "", service: "", date: "", message: "",
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitErrorDetail, setSubmitErrorDetail] = useState(null);
  const [showRecaptchaInfo, setShowRecaptchaInfo] = useState(false);
  const talonInputRef = useRef(null);
  const [talonChosen, setTalonChosen] = useState(null);
  const [talonClientReject, setTalonClientReject] = useState("");
  const [successEmailIssue, setSuccessEmailIssue] = useState({ show: false, detail: "" });
  const [successAttachmentSkipDb, setSuccessAttachmentSkipDb] = useState(false);
  useEffect(() => {
    if (!initialService) return;
    setForm((prev) => ({ ...prev, service: initialService }));
  }, [initialService]);

  useEffect(() => {
    trackEvent("booking_form_view", { section: "booking" });
  }, []);

  useEffect(() => {
    let cancelled = false;
    prefetchRecaptchaPublicConfig().finally(() => {
      if (cancelled) return;
      setShowRecaptchaInfo(shouldShowRecaptchaPublicNotice());
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    if (fieldErrors[k]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    }
    if ((status === "phone_error" || status === "error") && k === "phone") {
      setStatus(null);
    }
  };

  const phoneDigitsCount = form.phone.replace(/\D/g, "").length;
  const phoneLooksInvalid = form.phone !== "" && (!PHONE_REGEX.test(form.phone) || phoneDigitsCount < 7);

  const clearTalon = () => {
    setTalonChosen(null);
    setTalonClientReject("");
    if (talonInputRef.current) {
      talonInputRef.current.value = "";
    }
  };

  const onTalonPick = (e) => {
    const input = e.target;
    const file = input.files && input.files[0];
    setTalonClientReject("");

    if (!file) {
      setTalonChosen(null);

      return;
    }

    const norm = normalizeTalonExt(file.name);
    if (!["jpg", "png", "webp", "pdf"].includes(norm)) {
      setTalonClientReject("type");
      input.value = "";
      setTalonChosen(null);

      return;
    }

    const isPdf = norm === "pdf";

    if (isPdf && file.size > BOOKING_TALON_MAX_BYTES) {
      setTalonClientReject("size");
      input.value = "";
      setTalonChosen(null);

      return;
    }

    if (!isPdf && file.size > BOOKING_ATTACHMENT_MAX_IMAGE_BEFORE_COMPRESS) {
      setTalonClientReject("size_camera");
      input.value = "";
      setTalonChosen(null);

      return;
    }

    setTalonChosen({
      name: file.name || `fisier.${norm}`,
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const missing = {};
    if (!form.name.trim()) missing.name = true;
    if (!form.email.trim()) missing.email = true;
    if (!form.phone.trim()) missing.phone = true;
    if (!form.service.trim()) missing.service = true;

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setStatus("error");
      return;
    }
    const phoneDigitsCount = form.phone.replace(/\D/g, "").length;
    if (!PHONE_REGEX.test(form.phone) || phoneDigitsCount < 7) {
      setPhoneTouched(true);
      setStatus("phone_error");
      return;
    }

    setIsSubmitting(true);
    setStatus(null);
    setSubmitErrorDetail(null);
    setSuccessEmailIssue({ show: false, detail: "" });
    setSuccessAttachmentSkipDb(false);
    trackEvent("booking_submit_click", {
      service: form.service,
      has_message: Boolean(form.message),
      has_date: Boolean(form.date),
      has_attachment: Boolean(talonInputRef.current?.files?.[0]),
    });

    try {
      const apiBaseUrl = resolveApiBaseUrl({ forTracking: false });
      invalidateRecaptchaPublicConfig();
      await prefetchRecaptchaPublicConfig();
      const { token: captcha_token, executeError } = await getRecaptchaTokenResult("booking_submit");
      if (!captcha_token && executeError) {
        setSubmitErrorDetail(executeError);
        setStatus("submit_error");
        trackEvent("booking_submit_error", {
          service: form.service,
          status: 0,
          recaptcha_client_error: true,
        });
        return;
      }

      let talonFile = talonInputRef.current?.files?.[0];
      if (talonFile) {
        const norm = normalizeTalonExt(talonFile.name);
        if (!["jpg", "png", "webp", "pdf"].includes(norm)) {
          setTalonClientReject("type");
          return;
        }
        const isPdfUpload = norm === "pdf";

        if (isPdfUpload && talonFile.size > BOOKING_TALON_MAX_BYTES) {
          setTalonClientReject("size");
          return;
        }
        if (!isPdfUpload) {
          talonFile = await compressBookingImageForUpload(talonFile, BOOKING_TALON_MAX_BYTES);
        }
        if (talonFile.size > BOOKING_TALON_MAX_BYTES) {
          setTalonClientReject("size");
          return;
        }
      }

      const bookingPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        car: form.car.trim(),
        service: form.service.trim(),
        date: form.date.trim(),
        message: form.message.trim(),
        captcha_token: captcha_token || "",
      };

      let response;
      if (!talonFile) {
        /* Evită multipart când nu e atașament — unele stive Apache/PHP lasă $_POST gol. */
        response = await fetch(`${apiBaseUrl}/bookings.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json;charset=UTF-8" },
          body: JSON.stringify(bookingPayload),
        });
      } else {
        const fd = new FormData();
        /* Copie întreagă a câmpurilor pentru cazuri când multipart $_POST ajunge gol pe server dar JSON se parsează. */
        fd.append("booking_json", JSON.stringify(bookingPayload));
        fd.append("name", bookingPayload.name);
        fd.append("email", bookingPayload.email);
        fd.append("phone", bookingPayload.phone);
        fd.append("car", bookingPayload.car);
        fd.append("service", bookingPayload.service);
        fd.append("date", bookingPayload.date);
        fd.append("message", bookingPayload.message);
        fd.append("captcha_token", bookingPayload.captcha_token);
        fd.append("booking_attachment", talonFile, talonFile.name);

        response = await fetch(`${apiBaseUrl}/bookings.php`, {
          method: "POST",
          body: fd,
        });
      }

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSubmitErrorDetail(typeof data.error === "string" ? data.error : null);
        setStatus("submit_error");
        trackEvent("booking_submit_error", {
          service: form.service,
          status: response.status,
        });
        return;
      }

      if (data.ok === false) {
        setSubmitErrorDetail(typeof data.error === "string" ? data.error : null);
        setStatus("submit_error");
        trackEvent("booking_submit_error", {
          service: form.service,
          status: response.status,
        });
        return;
      }
      setStatus("success");
      const notifyOk = data.email_notify_ok !== false;
      const notifyDetail = typeof data.email_notify_detail === "string" ? data.email_notify_detail.trim() : "";
      setSuccessEmailIssue(
        !notifyOk ? { show: true, detail: notifyDetail } : { show: false, detail: "" },
      );
      setSuccessAttachmentSkipDb(data.attachment_skip_code === "db_column_missing");
      trackEvent("booking_submit_success", {
        service: form.service,
        has_attachment: Boolean(data.talon_uploaded || data.attachment_uploaded),
        email_notify_ok: notifyOk,
        attachment_skipped_db: data.attachment_skip_code === "db_column_missing",
      });
      setForm({ name: "", email: "", phone: "", car: "", service: "", date: "", message: "" });
      clearTalon();
      setTimeout(() => setStatus(null), 6000);
    } catch {
      setSubmitErrorDetail(null);
      setStatus("submit_error");
      trackEvent("booking_submit_error", {
        service: form.service,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitErrStr = typeof submitErrorDetail === "string" ? submitErrorDetail : "";
  const bookingRecaptchaSetupLike = isRecaptchaClientSetupErrorMessage(submitErrStr);
  const bookingShowDevDomainsHint =
    bookingRecaptchaSetupLike &&
    (/localhost/i.test(submitErrStr) ||
      /127\.0\.0\.1/i.test(submitErrStr) ||
      /supported domains/i.test(submitErrStr));

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="bg-[#f7f8fa] border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
              {bookingCopy.eyebrow}
            </div>
            <h2 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              {bookingCopy.title}
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md">
              {bookingCopy.lead}
            </p>

            <div className="mt-10 border-2 border-[#0b378c] bg-white p-6">
              <div className="flex items-center gap-3 text-[#0b378c] font-mono text-xs uppercase tracking-[0.3em]">
                <Wrench className="w-4 h-4" />
                {t.contact.service}
              </div>
              <a
                data-testid="booking-side-phone"
                href="tel:+40343101131"
                className="mt-3 inline-flex items-center gap-3 font-[Barlow_Condensed] font-bold text-3xl text-[#0b1220] hover:text-[#0b378c] transition-colors"
              >
                +40 343 101 131
              </a>
              <div className="mt-6 flex items-center gap-3 text-[#e3191b] font-mono text-xs uppercase tracking-[0.3em]">
                <Phone className="w-4 h-4" />
                {t.contact.serviceMobile}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  data-testid="booking-side-phone-mobile"
                  href="tel:+40753094642"
                  className="font-[Barlow_Condensed] font-bold text-3xl text-[#0b1220] hover:text-[#e3191b] transition-colors"
                >
                  +40 753 094 642
                </a>
                <WhatsAppTelCompanion
                  telHref="tel:+40753094642"
                  trackingChannel="booking_sidebar_mobile"
                  iconClassName="h-8 w-8"
                />
              </div>
              <div className="mt-6 flex items-center gap-3 text-[#0b378c] font-mono text-xs uppercase tracking-[0.3em]">
                <Phone className="w-4 h-4" />
                {t.contact.itp}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <a
                  href="tel:+40740050120"
                  className="font-[Barlow_Condensed] font-bold text-3xl text-[#0b1220] hover:text-[#0b378c] transition-colors"
                >
                  +40 740 050 120
                </a>
                <WhatsAppTelCompanion
                  telHref="tel:+40740050120"
                  trackingChannel="booking_sidebar_itp"
                  iconClassName="h-8 w-8"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="bg-white border border-[#e2e6ec] p-6 lg:p-10 shadow-sm"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field testid="field-name" label={t.booking.labels.name}
                  placeholder={bookingCopy.placeholders.name}
                  value={form.name} onChange={update("name")}
                  hasError={Boolean(fieldErrors.name)}
                />
                <Field testid="field-email" type="email" label={bookingCopy.labels.email}
                  placeholder={bookingCopy.placeholders.email}
                  value={form.email} onChange={update("email")}
                  hasError={Boolean(fieldErrors.email)}
                />
                <Field testid="field-phone" type="tel" label={bookingCopy.labels.phone}
                  placeholder={bookingCopy.placeholders.phone}
                  title={bookingCopy.phoneValidation}
                  value={form.phone}
                  onChange={update("phone")}
                  onBlur={() => setPhoneTouched(true)}
                  hasError={Boolean(fieldErrors.phone) || (phoneTouched && phoneLooksInvalid)}
                />
                {phoneTouched && phoneLooksInvalid && (
                  <div className="sm:col-span-2 -mt-2 text-xs text-[#c01416] font-mono uppercase tracking-[0.08em]">
                    {bookingCopy.phoneValidation}
                  </div>
                )}
                <Field testid="field-car" label={bookingCopy.labels.car}
                  placeholder={bookingCopy.placeholders.car}
                  value={form.car} onChange={update("car")} />
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-2 block">
                    {bookingCopy.labels.service}
                  </label>
                  <select
                    data-testid="field-service"
                    value={form.service}
                    onChange={update("service")}
                    className={`w-full bg-[#f7f8fa] border text-[#0b1220] focus:outline-none focus:ring-1 h-12 px-4 font-mono text-sm ${
                      fieldErrors.service
                        ? "border-[#e3191b] focus:border-[#e3191b] focus:ring-[#e3191b]"
                        : "border-[#e2e6ec] focus:border-[#0b378c] focus:ring-[#0b378c]"
                    }`}
                  >
                    <option value="">{bookingCopy.placeholders.service}</option>
                    {t.services.items.map((s) => (
                      <option key={s.code} value={s.title}>{s.title}</option>
                    ))}
                  </select>
                </div>
                <Field testid="field-date" type="date" label={bookingCopy.labels.date}
                  value={form.date} onChange={update("date")} />
                <div className="sm:col-span-2">
                  <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-2 block">
                    {bookingCopy.labels.message}
                  </label>
                  <textarea
                    data-testid="field-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder={bookingCopy.placeholders.message}
                    rows={4}
                    className="w-full bg-[#f7f8fa] border border-[#e2e6ec] text-[#0b1220] focus:border-[#0b378c] focus:outline-none focus:ring-1 focus:ring-[#0b378c] px-4 py-3 font-mono text-sm placeholder:text-slate-400 resize-none"
                  />
                </div>
                <div className="sm:col-span-2 border-t border-[#eef2f6] pt-5 mt-1">
                  <label
                    htmlFor="booking-attachment-input"
                    className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-2 block"
                  >
                    {bookingCopy.talonLabel}
                  </label>
                  <p className="text-xs text-slate-600 leading-relaxed">{bookingCopy.talonHint}</p>
                  <p className="mt-1 text-[10px] text-slate-500 font-mono leading-relaxed">
                    {bookingCopy.talonCaptureNote}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => talonInputRef.current?.click()}
                      className="inline-flex items-center justify-center border border-[#0b378c] bg-[#f0f4fb] text-[#0b378c] font-mono text-xs uppercase tracking-wider px-4 py-2.5 hover:bg-[#e2e8ff] transition-colors"
                    >
                      {bookingCopy.talonBrowse}
                    </button>
                    {talonChosen ? (
                      <>
                        <span
                          className="text-xs font-mono text-slate-700 max-w-[min(100%,280px)] truncate"
                          title={talonChosen.name}
                        >
                          {talonChosen.name}
                        </span>
                        <button
                          type="button"
                          onClick={clearTalon}
                          className="text-xs font-mono uppercase tracking-wider text-[#c01416] hover:underline"
                        >
                          {bookingCopy.talonClear}
                        </button>
                      </>
                    ) : null}
                  </div>
                  <input
                    id="booking-attachment-input"
                    ref={talonInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf"
                    capture="environment"
                    onChange={onTalonPick}
                    className="sr-only"
                    data-testid="field-booking-attachment"
                  />
                  {talonClientReject === "size_camera" ? (
                    <p className="mt-2 text-xs text-[#c01416] font-mono">{bookingCopy.talonErrSizeCamera}</p>
                  ) : null}
                  {talonClientReject === "size" ? (
                    <p className="mt-2 text-xs text-[#c01416] font-mono">{bookingCopy.talonErrSize}</p>
                  ) : null}
                  {talonClientReject === "type" ? (
                    <p className="mt-2 text-xs text-[#c01416] font-mono">{bookingCopy.talonErrType}</p>
                  ) : null}
                </div>
              </div>

              {showRecaptchaInfo ? (
                <div
                  data-testid="booking-recaptcha-info"
                  className="mt-6 rounded border border-[#c7d2eb] bg-[#f0f4fb] px-4 py-4"
                >
                  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#0b378c] mb-2">
                    {bookingCopy.recaptchaTitle}
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed">{bookingCopy.recaptchaBody}</p>
                  <p className="mt-3 text-[10px] text-slate-600 leading-relaxed">
                    <span>{bookingCopy.recaptchaLegalLead}</span>{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      className="text-[#0b378c] underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {bookingCopy.recaptchaPrivacy}
                    </a>
                    {" · "}
                    <a
                      href="https://policies.google.com/terms"
                      className="text-[#0b378c] underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {bookingCopy.recaptchaTerms}
                    </a>
                  </p>
                </div>
              ) : null}

              {status === "error" && (
                <div data-testid="booking-error"
                  className="mt-5 border border-[#e3191b] bg-red-50 text-[#c01416] px-4 py-3 font-mono text-xs uppercase tracking-wider">
                  {bookingCopy.validation}
                </div>
              )}
              {status === "phone_error" && (
                <div data-testid="booking-phone-error"
                  className="mt-5 border border-[#e3191b] bg-red-50 text-[#c01416] px-4 py-3 font-mono text-xs uppercase tracking-wider">
                  {bookingCopy.phoneValidation}
                </div>
              )}
              {status === "submit_error" && bookingRecaptchaSetupLike ? (
                <div
                  data-testid="booking-submit-error"
                  className="mt-5 rounded-xl border-2 border-red-600 bg-red-50 px-4 py-5 sm:px-6 sm:py-6 text-red-900 shadow-sm"
                  role="alert"
                >
                  {bookingShowDevDomainsHint ? (
                    <p className="text-sm sm:text-base font-semibold text-red-950 leading-snug">
                      {bookingCopy.recaptchaLocalhostHint}
                    </p>
                  ) : null}
                  <p
                    className={`text-base sm:text-lg md:text-xl font-medium leading-snug break-words ${bookingShowDevDomainsHint ? "mt-3" : ""}`}
                  >
                    {submitErrorDetail || bookingCopy.submitErrorDefault}
                  </p>
                </div>
              ) : null}
              {status === "submit_error" && !bookingRecaptchaSetupLike ? (
                <div
                  data-testid="booking-submit-error"
                  className="mt-5 rounded-lg border border-[#e3191b] bg-red-50 text-[#c01416] px-4 py-3 text-sm sm:text-base leading-snug break-words"
                >
                  {submitErrorDetail || bookingCopy.submitErrorDefault}
                </div>
              ) : null}
              {status === "success" && (
                <div className="mt-5 space-y-3">
                  <div
                    data-testid="booking-success"
                    className="border border-[#0b378c] bg-[#eaf0fb] text-[#0b378c] px-4 py-3 font-mono text-xs uppercase tracking-wider"
                  >
                    {bookingCopy.success}
                  </div>
                  {successAttachmentSkipDb ? (
                    <div
                      data-testid="booking-success-attachment-skip"
                      className="rounded-lg border border-amber-600 bg-amber-50 text-amber-950 px-4 py-3 text-sm leading-snug"
                      role="status"
                    >
                      <p className="font-semibold">{bookingCopy.attachmentSkippedDbTitle}</p>
                      <p className="mt-2 text-amber-950/95">{bookingCopy.attachmentSkippedDbBody}</p>
                    </div>
                  ) : null}
                  {successEmailIssue.show ? (
                    <div
                      data-testid="booking-success-email-warning"
                      className="rounded-lg border border-amber-500 bg-amber-50 text-amber-950 px-4 py-3 text-sm leading-snug"
                      role="status"
                    >
                      <p className="font-semibold text-amber-950">{bookingCopy.successEmailNotifyFailed}</p>
                      {successEmailIssue.detail ? (
                        <p className="mt-2 font-mono text-xs text-amber-900/90 break-words whitespace-pre-wrap">
                          {successEmailIssue.detail}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={isSubmitting}
                className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#e3191b] hover:bg-[#c01416] text-white font-bold uppercase tracking-wider text-sm px-8 py-4 transition-colors"
              >
                {isSubmitting ? "Se trimite..." : bookingCopy.submit}
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ testid, label, type = "text", value, onChange, onBlur, placeholder, pattern, title, hasError = false }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-2 block">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        pattern={pattern}
        title={title}
        className={`w-full bg-[#f7f8fa] border text-[#0b1220] focus:outline-none focus:ring-1 h-12 px-4 font-mono text-sm placeholder:text-slate-400 ${
          hasError
            ? "border-[#e3191b] focus:border-[#e3191b] focus:ring-[#e3191b]"
            : "border-[#e2e6ec] focus:border-[#0b378c] focus:ring-[#0b378c]"
        }`}
      />
    </div>
  );
}
